import { useEffect, useMemo, useRef, useState } from 'react';
import { Chip, Stack } from '@mui/material';
import { format } from 'date-fns';
import { useSchedulerContext } from '../context/SchedulerContext';
import { SchedulerEvent, SchedulerResource } from '../types/scheduler.types';
import { pickClosestEventToToday } from '../utils/claimSearchHelpers';

const FLASH_CLASS = 'claim-search-flash';
const FLASH_DURATION_MS = 1000;
const RECONFIGURE_EVENT = 'endReconfigure';

export interface ClaimSearchChipsProps {
  /** The claim number being searched */
  claimNumber: string | null | undefined;
  /** All scheduler events (matching + surrounding context) */
  events: SchedulerEvent[];
  /** Resources, used to resolve resource names for chip labels */
  resources: SchedulerResource[];
  /**
   * Called when the user clicks a chip — lets the parent recenter the
   * scheduler's date range on the chosen task. The imperative scroll +
   * flash run after the scheduler's `endReconfigure` event so they apply
   * to the freshly-rendered events.
   */
  onSelectEvent?: (event: SchedulerEvent) => void;
}

/**
 * Chip row shown directly to the right of QuickFilters when a claim search
 * is active. Each chip represents one task whose claimNumber matches the
 * search; clicking jumps the scheduler to that task and flashes the event.
 *
 * Ordered descending by start date (newest first). Hidden when no claim
 * search is active or no matches exist.
 *
 * Must be rendered inside SchedulerProvider.
 */
export function ClaimSearchChips({
  claimNumber,
  events,
  resources,
  onSelectEvent,
}: ClaimSearchChipsProps) {
  const { schedulerRef } = useSchedulerContext();
  const [activeChipId, setActiveChipId] = useState<string | null>(null);
  // Tracks the claim we've already auto-selected a chip for, so a fresh
  // claim search picks the closest-to-today chip but a user's subsequent
  // chip clicks aren't overwritten on rerender.
  const autoSelectedClaimRef = useRef<string | null>(null);
  // Tracks any pending endReconfigure listener so a subsequent chip
  // click can deregister it before installing its own — avoids stacked
  // listeners when the user clicks several chips in quick succession or
  // clicks a chip whose date matches the current visible week (no
  // reconfigure fires; the listener would otherwise sit until the next
  // unrelated reconfigure).
  const reconfigureListenerRef = useRef<(() => void) | null>(null);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const matchingEvents = useMemo(() => {
    if (!claimNumber) return [];
    const resourceNameById = new Map(
      resources.map((r) => [r.id as string, r.name as string])
    );

    return events
      .filter(
        (e) =>
          (e as { claimNumber?: string }).claimNumber === claimNumber &&
          e.startDate
      )
      .map((e) => ({
        event: e,
        resourceName: resourceNameById.get(e.resourceId as string) ?? '',
        startDate: new Date(e.startDate as string | Date),
      }))
      .sort((a, b) => b.startDate.getTime() - a.startDate.getTime());
  }, [claimNumber, events, resources]);

  // Reset active chip when the claim search is cleared
  useEffect(() => {
    if (!claimNumber) {
      setActiveChipId(null);
      autoSelectedClaimRef.current = null;
    }
  }, [claimNumber]);

  // On a fresh claim search, mark the auto-jumped chip (closest to today,
  // future-preferred — same rule the auto-navigate effect uses) as active
  // so the user can see which task the scheduler initially focused on.
  // Runs once per claim, then yields to user clicks.
  useEffect(() => {
    if (!claimNumber || autoSelectedClaimRef.current === claimNumber) return;
    const matched = pickClosestEventToToday(events, claimNumber);
    if (matched?.id) {
      autoSelectedClaimRef.current = claimNumber;
      setActiveChipId(matched.id as string);
    }
  }, [claimNumber, events]);

  // Drop any pending listener / timer on unmount
  useEffect(() => {
    return () => {
      const scheduler = schedulerRef.current?.instance as
        | { un: (event: string, fn: () => void) => void }
        | undefined;
      if (scheduler && reconfigureListenerRef.current) {
        scheduler.un(RECONFIGURE_EVENT, reconfigureListenerRef.current);
        reconfigureListenerRef.current = null;
      }
      if (flashTimerRef.current) {
        clearTimeout(flashTimerRef.current);
      }
    };
  }, [schedulerRef]);

  if (!claimNumber || matchingEvents.length === 0) {
    return null;
  }

  const handleChipClick = (event: SchedulerEvent) => {
    const eventId = event.id as string;
    setActiveChipId(eventId);

    if (!event.startDate) return;

    const scheduler = schedulerRef.current?.instance as
      | {
          on: (event: string, fn: () => void) => void;
          un: (event: string, fn: () => void) => void;
          scrollToDate: (date: Date, options: object) => void;
          scrollResourceIntoView: (resource: unknown, options: object) => void;
          resourceStore?: { getById: (id: string) => unknown };
          eventStore?: {
            getById: (id: string) =>
              | {
                  cls?: {
                    add: (c: string) => void;
                    remove: (c: string) => void;
                  };
                }
              | undefined;
          };
        }
      | undefined;

    if (!scheduler) {
      onSelectEvent?.(event);
      return;
    }

    const performScrollAndFlash = () => {
      try {
        scheduler.scrollToDate(new Date(event.startDate as string | Date), {
          block: 'center',
          animate: true,
        });

        const resourceId = event.resourceId as string | undefined;
        if (resourceId) {
          const resourceRecord = scheduler.resourceStore?.getById(resourceId);
          if (resourceRecord) {
            scheduler.scrollResourceIntoView(resourceRecord, { animate: true });
          }
        }

        // Imperative flash — toggle a transient class on the event
        // record so the user's eye lands on the just-scrolled-to task.
        // Bypasses the React data flow because the highlight is one-shot
        // UI feedback, not part of the events model.
        const eventRecord = scheduler.eventStore?.getById(eventId);
        if (eventRecord?.cls) {
          eventRecord.cls.add(FLASH_CLASS);
          if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
          flashTimerRef.current = setTimeout(() => {
            flashTimerRef.current = null;
            eventRecord.cls?.remove(FLASH_CLASS);
          }, FLASH_DURATION_MS);
        }
      } catch (error) {
        if (process.env['NODE_ENV'] === 'development') {
          // eslint-disable-next-line no-console
          console.warn(
            '[ClaimSearchChips] Failed to scroll/flash event:',
            error
          );
        }
      }
    };

    // Wait for Bryntum's time axis to finish reconfiguring after the
    // parent applies the new date range — `endReconfigure` fires after
    // both the data update and the UI catch up. If the new range equals
    // the current one (chip already focused), no reconfigure fires and
    // the scroll/flash simply skips — acceptable since the scheduler is
    // already showing that task.
    if (reconfigureListenerRef.current) {
      scheduler.un(RECONFIGURE_EVENT, reconfigureListenerRef.current);
    }
    const onReconfigureDone = () => {
      scheduler.un(RECONFIGURE_EVENT, onReconfigureDone);
      reconfigureListenerRef.current = null;
      performScrollAndFlash();
    };
    reconfigureListenerRef.current = onReconfigureDone;
    scheduler.on(RECONFIGURE_EVENT, onReconfigureDone);

    // Trigger the parent's setDateRange last so the listener above is
    // registered before Bryntum reacts to the new prop.
    onSelectEvent?.(event);
  };

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ flexWrap: 'wrap', rowGap: 1 }}
    >
      {matchingEvents.map(({ event, resourceName, startDate }) => {
        const eventId = event.id as string;
        const isActive = activeChipId === eventId;
        const label = resourceName
          ? `${format(startDate, 'MMM d')} — ${resourceName}`
          : format(startDate, 'MMM d');

        return (
          <Chip
            key={eventId}
            label={label}
            size="small"
            color="primary"
            variant={isActive ? 'filled' : 'outlined'}
            onClick={() => handleChipClick(event)}
            clickable
          />
        );
      })}
    </Stack>
  );
}
